import {
	Button,
	DataGridButton,
	DataGridButtonType,
	StylovyzeSearchBar,
	getSubscriptions,
	useCompanyDateTime,
} from '@innovyze/stylovyze';
import { StyledGrid, rem } from '@Utils/styles';

import { Grid, Stack } from '@mui/material';
import React, { useState } from 'react';
import { viewerAccess } from '@innovyze/shared-utils';
import { useGlobalization } from '@Translations/useGlobalization';
import { Add } from '@mui/icons-material';
import { useStyles } from '@Components/DecisionTree/DecisionTree.style';
import { selectCurrentTree, selectIsLoading } from '@Selectors/Rehab.selectors';
import { AGSetMode } from '@Types/rehab.types';
import { useRehabResultSelectors } from '@Components/RehabResult/useRehabResultsSelectors';
import { type useGridApiRef } from '@mui/x-data-grid-premium';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';

interface TableToolbarProps {
	searchString: string;
	onSearch: (searchString: string) => void;
	showFilterPanel: () => void;
	filterRef: React.RefObject<HTMLButtonElement>;
	hideNewGroup: boolean;
	onNewGroup: () => void;
	isSaveDisabled: boolean;
	isRunDisabled: boolean;
	isRehabTreeValidating: boolean;
	isRehabTreeRunning: boolean;
	onSave: () => void;
	onRun: () => void;
	apiRef: ReturnType<typeof useGridApiRef>;
	lastRunCompleted?: string;
}

export const TableToolbar = ({
	searchString,
	onSearch,
	showFilterPanel,
	filterRef,
	onNewGroup,
	isSaveDisabled,
	isRunDisabled,
	isRehabTreeValidating,
	isRehabTreeRunning,
	onSave,
	onRun,
	apiRef,
	lastRunCompleted,
}: TableToolbarProps): JSX.Element => {
	const { t } = useGlobalization();
	const [searchValue, setSearchValue] = useState<string>(searchString);
	const subscriptions = getSubscriptions();
	const hasViewerAccess = viewerAccess(subscriptions);
	const classes = useStyles();
	const isTreeLoading = selectIsLoading();
	const { isLoading } = useRehabResultSelectors(true);
	const { mode } = selectCurrentTree();
	const { formatDate } = useCompanyDateTime();

	const onChange = (query: string) => {
		setSearchValue(query);
		onSearch(query);
	};

	const onRequestSearch = () => {
		onSearch(searchValue);
	};

	const onCancelSearch = () => {
		setSearchValue('');
		onSearch('');
	};

	const handleExportCsv = () => {
		if (apiRef.current && apiRef.current.exportDataAsCsv) {
			apiRef.current.exportDataAsCsv({
				fileName: 'groups-export',
			});
		}
	};

	const lastRunCompletedDiv = () => {
		if (lastRunCompleted && lastRunCompleted.length !== 0) {
			return (
				<div style={{ paddingTop: '6px' }}>
					{t('Results below were created on {{datetime}}', {
						datetime: formatDateTimeWithFormatDate(
							lastRunCompleted,
							formatDate,
						),
					})}
				</div>
			);
		}
		return null;
	};

	return (
		<StyledGrid
			container
			alignItems="center"
			justifyContent="space-between"
			style={{ padding: '1.5rem', flexWrap: 'nowrap' }}>
			<Grid container item xs={4}>
				<Stack>
					<StylovyzeSearchBar
						placeholder={t('Search')}
						onChange={onChange}
						onRequestSearch={onRequestSearch}
						onCancelSearch={onCancelSearch}
						value={searchValue}
						dataCy="search-01"
						style={{ width: rem(300) }}
					/>
					{lastRunCompletedDiv()}
				</Stack>
			</Grid>

			<Grid
				item
				container
				alignItems="center"
				justifyContent="flex-end"
				spacing={1}
				wrap="nowrap"
				style={{ flex: '1 1 auto', marginLeft: '1rem' }}>
				<Grid item>
					<DataGridButton
						type={DataGridButtonType.FILTER}
						disabled={isLoading || isTreeLoading}
						data-cy="filterButton"
						onClick={showFilterPanel}
						ref={filterRef}
					/>
				</Grid>
				<Grid item>
					<DataGridButton
						type={DataGridButtonType.EXPORT}
						disabled={isLoading || isTreeLoading}
						data-cy="exportCsvButton"
						onClick={handleExportCsv}
					/>
				</Grid>
				{!hasViewerAccess && mode === AGSetMode.CUSTOM && (
					<Grid item>
						<Button
							variant="contained"
							startIcon={<Add />}
							onClick={onNewGroup}
							disabled={isLoading || isTreeLoading}>
							{t('Group')}
						</Button>
					</Grid>
				)}
				{!hasViewerAccess && (
					<Grid item>
						<Button
							variant="outlined"
							color="secondary"
							className={classes.treeActionsRightSave}
							onClick={onSave}
							disabled={isSaveDisabled}>
							{isRehabTreeValidating
								? t('Previewing')
								: t('Save')}
						</Button>
						<Button
							variant="contained"
							className={classes.treeActionsRightRun}
							disabled={isRunDisabled}
							data-cy="button-NewTree"
							color="primary"
							onClick={onRun}>
							{isRehabTreeRunning ? t('Running') : t('Run')}
						</Button>{' '}
					</Grid>
				)}
			</Grid>
		</StyledGrid>
	);
};
